import PageLoader from '@/common/service/flow/page-loader';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { loadSelectByPersist } from '@/common/service/plan-data-mapper';
import { buildAccountBaseService } from '@/common/service/base-paid';
import Flow from '@/flows/flow';
import { addToCartByCategoryAndBillingCycle } from '@/common/service/flow/utils';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { UpdateTypes, updatePlansCheckout } from '@/common/service/checkout-utils';
import store from '@/store/index';
import { parseConsumptionCode } from '@/plans/EVENT_SAAS/helper/parse';
import { $query, $config, $context } from '@/global-install';
import { BILLING_CYCLE, planTypes, VIEWS } from '@/common/constants';
import { checkUpcomingWebinars } from '@/apis';
import { translation } from '@/plugins/i18n';
import BillingMessageBox from '@/components/common/message-box';
import { capitalizeFirstLetter } from '@/common/utils';
import EventTracking from '@/aspects/trackings';
import { getAssignLicenseResult } from '@/common/service/validate/assign-license';
import _ from 'lodash';

//  1 mean can switch no banner, 2 mean has paid event block switch, 3 mean has free event with banner
const UpcomingWebinarPromptMap = {
  CAN_SWITCH: 1,
  PAID_WITH_MANDATORY_WARNING: 2,
  FREE_WITH_SOFT_WARNING: 3,
};

const loadDefaultSelectByQuery = (boughtPlans) => {
  return (planList = []) => {
    const { from } = pageConfig || {};
    const targetCategory = from;
    const targetPlanType = getPlanTypeByCategory(from);
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    const oldPlanCode = $query?.oldPlanCode;
    const oldPlan = boughtPlans.find(p => p.plan_code === oldPlanCode);
    const billingCycle = oldPlan?.billing_cycle || BILLING_CYCLE.ANNUAL;
    const defaultLicense = oldPlan?.cartInfo?.selected_number;

    if (!hasSelected) {
      addToCartByCategoryAndBillingCycle(planList, targetPlanType, targetCategory, billingCycle, defaultLicense);
      return planList;
    }

    return planList;
  };
};


async function initShoppingCartForSwitch(state) {
  const { persist } = state;

  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  const oldPlanCode = pageConfig?.oldPlanCode;
  const currentEditPlan = _.find(boughtPlans, (plan) => plan.plan_code === oldPlanCode) || {};

  if (pageConfig?.editConfig?.planType === planTypes.WEBINAR) {
    if (!currentEditPlan?.plan_code) {
      location.href = '/billing';
      return;
    }
  }

  const assignResult = await getAssignLicenseResult(currentEditPlan);

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery(boughtPlans));

  const planList = await loader.dispatch();

  return {
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
    assignResult
  };
}
function checkoutForSwitch(submitParams) {
  const planList = store.getters.getSelectedPlans.map(item => {
    if(item.isPPA) {
      const { plan_code, attendees } = parseConsumptionCode(item.plan_code)
      return {
        quantity: attendees,
        planCode: plan_code,
        oldPlanCode: pageConfig.oldPlanCode,
      }
    }
    return {
      quantity: item.cartInfo.selected_number,
      planCode: item.plan_code,
      oldPlanCode: pageConfig.oldPlanCode,
    }
  })

  return updatePlansCheckout(submitParams, planList, UpdateTypes.SWITCH);
}

const preCheckUpcomingWebinar = async () => {
  if ($context.type === planTypes.WEBINAR) {
    const getters = store.getters;
    const oldPlanCode = $config('oldPlanCode');
    const newPlan = getters.getSelectedPlans[0] || {};
    const targetPlanCode = newPlan?.plan_code;
    const { status, result, errorMessage } = await checkUpcomingWebinars({srcPlanCode: oldPlanCode, targetPlanCode});
    const newPlanCapacity = newPlan.capacity;
    const newPlanName = `${translation(newPlan.planNameKey)} ${newPlanCapacity} ${capitalizeFirstLetter(translation('billing.attendees'))}`;
    const oldPlan = getters.boughtPlans.find(p => p.plan_code === oldPlanCode) || {};
    const oldPlanCapacity = oldPlan.capacity;
    const oldPlanName = `${translation(oldPlan.planNameKey)} ${oldPlanCapacity} ${capitalizeFirstLetter(translation('billing.attendees'))}`;

    if (status !== true) {
      return Promise.reject(errorMessage || 'System error, please try again later.');
    }

    let title = translation('billing.zoom_events.upgrade.prompt_title'),
        promptMsg = '',
        confirmButtonText = translation('billing.webinar.upcoming_prompt.yes_place_order'),
        cancelButtonText = translation('billing.webinar.upcoming_prompt.no_go_back');
    if (result === UpcomingWebinarPromptMap.CAN_SWITCH) {
      return Promise.resolve();
    } else if (result === UpcomingWebinarPromptMap.FREE_WITH_SOFT_WARNING) {
      promptMsg = translation('billing.webinar.upcoming_prompt.has_free_event_soft_desc', [
        newPlanCapacity,
      ]);
    } else if (result === UpcomingWebinarPromptMap.PAID_WITH_MANDATORY_WARNING) {
      title = translation('billing.webinar.upcoming_prompt.has_paid_event_block_title');
      confirmButtonText = translation('billing.webinar.upcoming_prompt.exit_checkout_cta');
      cancelButtonText = translation('billing.webinar.upcoming_prompt.cancel_webinar_plan_cta', [oldPlanCapacity]);
      promptMsg = translation('billing.webinar.upcoming_prompt.has_paid_event_block_desc', [
        oldPlanName,
        newPlanName,
        newPlanCapacity,
      ]);
    }

    let type = '';

    if (promptMsg) {
      const buyInteraction = 'w100 downsell', buySelection = `${newPlanName} are you sure modal`
      result === UpcomingWebinarPromptMap.PAID_WITH_MANDATORY_WARNING ?
          EventTracking.buyInteraction(buyInteraction, `${newPlanName} - no eligible modal - impression`)
          :
          EventTracking.buyInteraction(buyInteraction, `${buySelection} - impression`);
      return BillingMessageBox.confirm(promptMsg, title, {
        dangerouslyUseHTMLString: true,
        confirmButtonText,
        cancelButtonText,
        closeOnHashChange: false,
        closeOnPressEscape: false,
        closeOnClickModal: false,
      })
        .then((action) => {
          type = action;
          if (result === UpcomingWebinarPromptMap.PAID_WITH_MANDATORY_WARNING) {
            EventTracking.buyInteraction(buyInteraction, `${newPlanName} - no eligible modal - exit checkout`);
            location.href = `/billing?viewPlanCode=${oldPlanCode}`;
            return Promise.reject();
          }
          EventTracking.buyInteraction(buyInteraction, `${buySelection} - yes,place order`);
          return Promise.resolve();
        })
        .catch((action) => {
          if (type) return Promise.reject();
          type = action;
          if (result === UpcomingWebinarPromptMap.PAID_WITH_MANDATORY_WARNING) {
            EventTracking.buyInteraction(buyInteraction, `${newPlanName} - no eligible modal - ${cancelButtonText}`);
            location.href = `/billing?cancelPlanCode=${oldPlanCode}`;
            return Promise.reject();
          }
          EventTracking.buyInteraction(buyInteraction, `${buySelection} - no,go back`);
          location.href = `/billing`;
          return Promise.reject();
        });
    }
  }
  return Promise.resolve();
}

const preCheck = async () => {
  await preCheckUpcomingWebinar();
}

export default {
  [flowTypes.switch]: class extends Flow {
    view() {
      return VIEWS.COMPOUND;
    }
    init() {
      return initShoppingCartForSwitch(...arguments);
    }

    beforeSubmit() {
      return preCheck(...arguments);
    }

    submit(submitParams) {
      return checkoutForSwitch(submitParams);
    }
  }
};
